import React from "react";
import { Facebook, Instagram, Mail, Phone, MapPin, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { Container } from "./Container";
import { LOCATIONS } from "../../lib/locations";
import { useCoreValues } from "./CoreValuesModal";
import { useGoogleReviews } from "../../lib/googleReviews";
import {
  CONTACT,
  LICENCES,
  NAV_LINKS,
  QVOLT_LOGO,
  NETCC_BADGE_SM,
  NETCC_URL,
  GOOGLE_G_LOGO,
  GOOGLE_REVIEWS,
  MAP_EMBED_SRC,
} from "../../lib/content";

const Col = ({ title, children, testId }) => (
  <div data-testid={testId}>
    <div className="qv-eyebrow text-subtle mb-5">{title}</div>
    <div className="flex flex-col gap-3 text-[14px] text-subtle">{children}</div>
  </div>
);

const GoogleReviewsPill = () => {
  const reviews = useGoogleReviews();
  return (
  <a
    href={reviews.url}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={`QVolt Solar, ${reviews.rating} stars from ${reviews.count} Google reviews`}
    data-testid="footer-google-reviews"
    className="qv-pill px-3.5 h-9"
  >
    <img
      src={GOOGLE_G_LOGO}
      alt=""
      aria-hidden="true"
      className="h-[16px] w-[16px] block"
    />
    <span className="font-semibold">
      {reviews.rating}
    </span>
    <Star
      className="h-3.5 w-3.5"
      strokeWidth={0}
      style={{ fill: GOOGLE_REVIEWS.star_color, color: GOOGLE_REVIEWS.star_color }}
    />
    <span className="text-subtle">
      ({reviews.count} reviews)
    </span>
  </a>
  );
};

const HeadOfficeMap = () => (
  <div data-testid="footer-head-office">
    <div className="qv-eyebrow text-subtle mb-5">Head office</div>
    <div className="relative" data-testid="footer-map">
      <iframe
        title="QVolt Solar head office location"
        src={MAP_EMBED_SRC}
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        allowFullScreen
        data-testid="footer-map-iframe"
        className="w-full h-[220px] rounded-xl border border-border block bg-white"
      />
      <div
        className="absolute top-3 left-3 inline-flex items-center gap-2 bg-white border border-border rounded-full shadow-sm pl-3 pr-4 h-9 pointer-events-none"
        style={{ zIndex: 10 }}
        data-testid="footer-map-overlay"
      >
        <MapPin className="h-3.5 w-3.5 text-brand-blue" strokeWidth={1.75} />
        <span className="text-[13px] font-medium text-ink">Head Office</span>
      </div>
    </div>
    <p
      className="mt-3 text-[13px] text-subtle leading-relaxed"
      data-testid="footer-map-address"
    >
      {CONTACT.address}
    </p>
  </div>
);

export const Footer = () => {
  const phoneClean = (CONTACT.phone || "").replace(/\s/g, "");
  const { open: openValues } = useCoreValues();
  return (
    <footer
      data-testid="site-footer"
      className="bg-muted border-t border-border"
    >
      <div
        className="border-b border-border bg-white"
        data-testid="footer-service-areas-band"
      >
        <Container className="py-4 md:py-5">
          <div className="flex flex-col md:flex-row md:items-center md:gap-3 text-[13px] text-subtle">
            <span className="qv-eyebrow text-brand-blue mb-2 md:mb-0">
              Service areas
            </span>
            <span className="flex-1 leading-relaxed">
              {LOCATIONS.map((l, i) => (
                <React.Fragment key={l.slug}>
                  <Link
                    to={`/solar-installers/${l.slug}`}
                    className="text-ink hover:text-brand-blue transition-colors qv-focus rounded-sm"
                    data-testid={`footer-area-${l.slug}`}
                  >
                    {l.name}
                  </Link>
                  {i < LOCATIONS.length - 1 && (
                    <span className="text-subtle/60 mx-2">·</span>
                  )}
                </React.Fragment>
              ))}
            </span>
          </div>
        </Container>
      </div>
      <Container className="py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-10">
          {/* Col 1 — Brand */}
          <div data-testid="footer-brand">
            <a
              href="/"
              className="inline-flex items-center mb-5 qv-focus rounded-md"
              data-testid="footer-logo-link"
            >
              <img
                src={QVOLT_LOGO}
                alt="QVolt Solar"
                width={184}
                height={42}
                loading="lazy"
                data-testid="footer-logo"
                className="h-10 w-auto block select-none"
                draggable={false}
              />
            </a>
            <p className="text-[14px] text-subtle leading-relaxed max-w-xs">
              Honest, expert solar for Queensland homeowners. Family owned.
              30-year workmanship warranty. NETCC approved.
            </p>
            <div className="flex items-center gap-2 mt-6">
              <a
                href="https://www.facebook.com/Qvoltsolar"
                aria-label="Facebook"
                data-testid="footer-social-facebook"
                target="_blank"
                rel="noreferrer noopener"
                className="h-9 w-9 rounded-full border border-border bg-white grid place-items-center text-ink hover:text-brand-blue hover:border-brand-blue transition-colors qv-focus"
              >
                <Facebook className="h-4 w-4" strokeWidth={1.5} />
              </a>
              <a
                href="https://www.instagram.com/qvoltsolar/"
                aria-label="Instagram"
                data-testid="footer-social-instagram"
                target="_blank"
                rel="noreferrer noopener"
                className="h-9 w-9 rounded-full border border-border bg-white grid place-items-center text-ink hover:text-brand-blue hover:border-brand-blue transition-colors qv-focus"
              >
                <Instagram className="h-4 w-4" strokeWidth={1.5} />
              </a>
            </div>
          </div>

          {/* Col 2 — Get in touch */}
          <Col title="Get in touch" testId="footer-contact">
            <a
              href={`tel:${phoneClean}`}
              className="hover:text-ink transition-colors inline-flex items-center gap-2 qv-focus rounded-sm"
              data-testid="footer-phone"
            >
              <Phone className="h-3.5 w-3.5" strokeWidth={1.75} />
              {CONTACT.phone}
            </a>
            <a
              href={`mailto:${CONTACT.email}`}
              className="hover:text-ink transition-colors inline-flex items-center gap-2 qv-focus rounded-sm"
              data-testid="footer-email"
            >
              <Mail className="h-3.5 w-3.5" strokeWidth={1.75} />
              {CONTACT.email}
            </a>
            <span className="inline-flex items-start gap-2">
              <MapPin
                className="h-3.5 w-3.5 mt-0.5 shrink-0"
                strokeWidth={1.75}
              />
              <span data-testid="footer-address">{CONTACT.address}</span>
            </span>
            <span
              className="text-[13px] text-subtle/80 mt-1"
              data-testid="footer-service-areas"
            >
              Service areas: {(CONTACT.service_areas || []).join(" · ")}
            </span>
          </Col>

          {/* Col 3 — Trust & licences + Quick links */}
          <Col title="Trust & licences" testId="footer-trust">
            <div className="flex flex-col gap-1.5">
              <span data-testid="footer-licence-qld">
                QLD electrical {LICENCES.QLD}
              </span>
              <span data-testid="footer-licence-nsw">NSW {LICENCES.NSW}</span>
            </div>
            <div
              className="mt-3 flex flex-wrap items-center gap-4"
              data-testid="footer-trust-badges"
            >
              <a
                href={NETCC_URL}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center group qv-focus rounded-full"
                data-testid="footer-netcc"
                aria-label="NETCC approved retailer"
              >
                <img
                  src={NETCC_BADGE_SM}
                  alt="NETCC approved retailer"
                  width={220}
                  height={144}
                  loading="lazy"
                  data-testid="footer-netcc-badge"
                  className="h-12 w-auto block opacity-95 group-hover:opacity-100 transition-opacity"
                />
              </a>
            </div>
            <div className="mt-3">
              <GoogleReviewsPill />
            </div>
            <div className="mt-5 pt-5 border-t border-border">
              <div className="qv-eyebrow text-subtle mb-3">Quick links</div>
              <div
                className="grid grid-cols-2 gap-x-6 gap-y-2"
                data-testid="footer-quicklinks"
              >
                {NAV_LINKS.map((l) => (
                  <Link
                    key={l.label}
                    to={l.href}
                    className="hover:text-ink transition-colors qv-focus rounded-sm"
                    data-testid={`footer-nav-${l.label.toLowerCase()}`}
                  >
                    {l.label}
                  </Link>
                ))}
                <Link
                  to="/energy-switch"
                  className="hover:text-ink transition-colors qv-focus rounded-sm"
                  data-testid="footer-nav-energy-plans"
                >
                  Energy plans
                </Link>
                <Link
                  to="/solar-export-limits"
                  className="hover:text-ink transition-colors qv-focus rounded-sm"
                  data-testid="footer-nav-export-checker"
                >
                  Export checker
                </Link>
                <Link
                  to="/solar-panel-checker"
                  className="hover:text-ink transition-colors qv-focus rounded-sm"
                  data-testid="footer-nav-panel-checker"
                >
                  Panel checker
                </Link>
                <Link
                  to="/rooftop-report"
                  className="hover:text-ink transition-colors qv-focus rounded-sm"
                  data-testid="footer-nav-rooftop-report"
                >
                  Rooftop Report
                </Link>
                <button
                  type="button"
                  onClick={openValues}
                  className="text-left hover:text-ink transition-colors qv-focus rounded-sm"
                  data-testid="footer-core-values-trigger"
                >
                  Our values
                </button>
              </div>
            </div>
          </Col>

          {/* Col 4 — Head office map */}
          <HeadOfficeMap />
        </div>

        <div
          className="mt-14 pt-6 border-t border-border flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[13px] text-subtle"
          data-testid="footer-bottom"
        >
          <span>
            © {new Date().getFullYear()} QVolt Solar. All rights reserved.
            <span className="text-subtle/60 mx-2">·</span>
            <Link
              to="/affiliate-disclosure"
              className="hover:text-ink transition-colors qv-focus rounded-sm"
              data-testid="footer-affiliate-disclosure"
            >
              Affiliate disclosure
            </Link>
          </span>
          <span className="text-subtle/80">Built for Queensland homes.</span>
        </div>
      </Container>
    </footer>
  );
};

export default Footer;
